import React, { useState, useEffect } from 'react';
import { vehicleService } from '../../api/services/vehicleService';
import { Question, Answer } from '../../../../shared/types';
import toast from 'react-hot-toast';
import { Loader2, X } from 'lucide-react';

interface QA {
  Id?: number;
  QuestionId: number;
  Question?: string;
  AnswerId: number;
  Answer?: string;
}

interface CargoPartForm {
  SeatRowCode: string;
  PriceCode: string;
  CostCode: string;
  FitmentCode: string;
  Extras: string;
  Notes: string;
}

interface Props {
  partId: number;
  partNumber: string;
  onClose: () => void;
  onSaved: () => void;
}

const ROW_OPTIONS = ['M', 'R'];
const CODE_OPTIONS = ['A', 'B'];

const EditCargoApplicationModal: React.FC<Props> = ({ partId, partNumber, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const [form, setForm] = useState<CargoPartForm>({
    SeatRowCode: '', PriceCode: '', CostCode: '', FitmentCode: '', Extras: '', Notes: '',
  });
  const [questionAnswers, setQuestionAnswers] = useState<QA[]>([]);

  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers,   setAnswers]   = useState<{ [qId: number]: Answer[] }>({});

  useEffect(() => { loadData(); }, [partId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      const [part, lookup] = await Promise.all([
        vehicleService.getCargoPartApplication(partId),
        vehicleService.getApplicationLookupData(),
      ]);
      setForm({
        SeatRowCode: part.SeatRowCode ?? '',
        PriceCode:   part.PriceCode   ?? '',
        CostCode:    part.CostCode    ?? '',
        FitmentCode: part.FitmentCode ?? '',
        Extras:      part.Extras      ?? '',
        Notes:       part.Notes       ?? '',
      });
      setQuestionAnswers(part.QuestionAnswers ?? []);
      setQuestions(lookup.questions ?? []);
      setAnswers(lookup.answers    ?? {});
    } catch {
      toast.error('Failed to load cargo application data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await vehicleService.updateCargoPartApplication(partId, form);
      toast.success('Cargo application saved');
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQA = async (qaId: number) => {
    await vehicleService.deleteCargoPartApplicationQuestion(partId, qaId);
    setQuestionAnswers(prev => prev.filter(q => q.Id !== qaId));
  };

  const handleAddQA = async () => {
    if (questions.length === 0) return;
    const firstQ = questions[0];
    const firstA = answers[firstQ.Id]?.[0];
    if (!firstA) return;
    const newId = await vehicleService.addCargoPartApplicationQuestion(partId, firstQ.Id, firstA.Id);
    setQuestionAnswers(prev => [...prev, {
      Id: newId, QuestionId: firstQ.Id, Question: firstQ.Name,
      AnswerId: firstA.Id, Answer: firstA.Name,
    }]);
  };

  const handleQAChange = async (qa: QA, field: 'QuestionId' | 'AnswerId', value: number) => {
    const newQuestionId = field === 'QuestionId' ? value : qa.QuestionId;
    const newAnswerId   = field === 'AnswerId'   ? value
      : (answers[newQuestionId]?.[0]?.Id ?? qa.AnswerId);

    if (qa.Id) await vehicleService.deleteCargoPartApplicationQuestion(partId, qa.Id);
    const newId = await vehicleService.addCargoPartApplicationQuestion(partId, newQuestionId, newAnswerId);

    const newQ = questions.find(q => q.Id === newQuestionId);
    const newA = answers[newQuestionId]?.find(a => a.Id === newAnswerId);
    setQuestionAnswers(prev => prev.map(q =>
      q.Id === qa.Id
        ? { Id: newId, QuestionId: newQuestionId, Question: newQ?.Name, AnswerId: newAnswerId, Answer: newA?.Name }
        : q
    ));
  };

  const sel = 'px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto pt-12 pb-8">
      <div className="bg-white dark:bg-gray-800 rounded shadow-xl w-full max-w-xl mx-4">

        {/* Header */}
        <div className="flex justify-between items-center px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Edit Cargo Application</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-7 h-7 animate-spin text-blue-600 dark:text-blue-400" />
          </div>
        ) : (
          <div className="px-5 py-5 space-y-4">

            {/* Row 1: Part # (read-only), Row, Price Code, Production Code */}
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Part #</label>
                <input value={partNumber} readOnly
                  className="px-2 py-1.5 border border-gray-200 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm w-24" />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Row</label>
                <select value={form.SeatRowCode}
                  onChange={e => setForm(p => ({ ...p, SeatRowCode: e.target.value }))}
                  className={`${sel} w-20`}>
                  <option value=""></option>
                  {ROW_OPTIONS.map(code => <option key={code} value={code}>{code}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Price Code</label>
                <select value={form.PriceCode}
                  onChange={e => setForm(p => ({ ...p, PriceCode: e.target.value }))}
                  className={`${sel} w-20`}>
                  <option value=""></option>
                  {CODE_OPTIONS.map(code => <option key={code} value={code}>{code}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Production Code</label>
                <select value={form.CostCode}
                  onChange={e => setForm(p => ({ ...p, CostCode: e.target.value }))}
                  className={`${sel} w-24`}>
                  <option value=""></option>
                  {CODE_OPTIONS.map(code => <option key={code} value={code}>{code}</option>)}
                </select>
              </div>
            </div>

            {/* Cargo Image (FitmentCode) */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Cargo Image</label>
              <input type="text" value={form.FitmentCode}
                onChange={e => setForm(p => ({ ...p, FitmentCode: e.target.value }))}
                maxLength={5}
                className={`${sel} w-32`} />
            </div>

            {/* Extras */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Extras</label>
              <input type="text" value={form.Extras}
                onChange={e => setForm(p => ({ ...p, Extras: e.target.value }))}
                className={`${sel} w-full`} />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes</label>
              <textarea value={form.Notes}
                onChange={e => setForm(p => ({ ...p, Notes: e.target.value }))}
                rows={3}
                className={`${sel} w-full resize-y`} />
            </div>

            {/* Questions */}
            <div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Questions</h3>
              <div className="space-y-2">
                {questionAnswers.map((qa, idx) => (
                  <div key={qa.Id ?? idx} className="flex items-center gap-2">
                    <button
                      onClick={() => qa.Id && handleDeleteQA(qa.Id)}
                      className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 shrink-0"
                    >
                      Delete
                    </button>
                    <select value={qa.QuestionId}
                      onChange={e => handleQAChange(qa, 'QuestionId', parseInt(e.target.value))}
                      className={`${sel} flex-1`}>
                      {questions.map(q => <option key={q.Id} value={q.Id}>{q.Name}</option>)}
                    </select>
                    <select value={qa.AnswerId}
                      onChange={e => handleQAChange(qa, 'AnswerId', parseInt(e.target.value))}
                      className={`${sel} flex-1`}>
                      {(answers[qa.QuestionId] ?? []).map(a => <option key={a.Id} value={a.Id}>{a.Name}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <button onClick={handleAddQA} disabled={questions.length === 0}
                className="mt-3 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-40">
                Add Question
              </button>
            </div>

          </div>
        )}

        {/* Footer */}
        {!loading && (
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750 rounded-b">
            <button onClick={onClose}
              className="px-4 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600">
              Close
            </button>
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        )}

      </div>
    </div>
  );
};

export default EditCargoApplicationModal;
